import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLeaf,
  faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";

import { AnimatedLogo } from "@aclymatepackages/atoms";

import PortfolioPurchaseFlow from "../../shared/purchase/PortfolioPurchaseFlow";
import PortfolioDisplayBlock from "../../modules/projects/PortfolioDisplayBlock";

import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import Link from "../../atoms/mui/Link";

import ScoutFab from "../scout/ScoutFab";

import { analyticsPage, triggerGoogleEvent } from "../../../helpers/analytics";
import { useAccountData } from "../../../helpers/firebase";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import usePortfoliosContext, {
  PortfoliosContextProvider
} from "../../../helpers/contexts/offsetsPortfolios";
import { deriveSurveyCompletion } from "../../../helpers/components/vendors";
import {
  computeTotalTons,
  filterEmissionsForYear,
  findSurveyEntry
} from "./dashboardHelpers";

const CnaughtAttributionBanner = () => (
  <Box p={4}>
    <Container>
      <Paper>
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <img
                alt="CNaught"
                src="/images/partner-logos/cnaught.svg"
                height={40}
              />
            </Grid>
            <Grid item>
              <Typography variant="body2">
                Carbon offsets powered by CNaught, a provider of science-backed
                carbon portfolios.
              </Typography>
              <Link href="https://www.cnaught.com/science-backed-portfolio">
                <Typography variant="body2">
                  Learn more about their approach
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  </Box>
);

const PortfolioLoadErrorBlock = () => (
  <Box p={4} display="flex" justifyContent="center">
    <Container maxWidth="sm">
      <Paper>
        <Box p={4}>
          <Grid container direction="column" spacing={2} alignItems="center">
            <Grid item>
              <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            </Grid>
            <Grid item>
              <Typography variant="h6" align="center">
                We couldn&apos;t load offset portfolios
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textSecondary" align="center">
                Something went wrong on our end. Please refresh the page or
                contact support@aclymate.com if the issue continues.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  </Box>
);

const LockedOffsetsBlock = () => (
  <Box p={4} display="flex" justifyContent="center">
    <Container maxWidth="sm">
      <Paper>
        <Box p={4}>
          <Grid container direction="column" spacing={2} alignItems="center">
            <Grid item>
              <FontAwesomeIcon icon={faLeaf} size="2x" />
            </Grid>
            <Grid item>
              <Typography variant="h6" align="center">
                Complete your emissions survey to unlock offsets
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textSecondary" align="center">
                Carbon offsets are the next step after you measure — finish your
                emissions survey and we&apos;ll show you portfolios sized to
                your footprint.
              </Typography>
            </Grid>
            <Grid item>
              <Link href="/explorer/emissions-survey">
                <Button variant="contained" color="secondary">
                  Take the emissions survey
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  </Box>
);

const TitleBlock = () => (
  <Typography variant="h5" color="textPrimary">
    Carbon Offsets
  </Typography>
);

const PortfolioTabs = () => {
  const {
    portfolios,
    portfoliosLoading,
    selectedPortfolio,
    onPortfolioSelect
  } = usePortfoliosContext();

  if (portfoliosLoading || !portfolios.length) {
    return null;
  }

  return (
    <Tabs
      value={selectedPortfolio?.id || false}
      onChange={(_, id) => onPortfolioSelect(id)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, py: 1 } }}
    >
      {portfolios.map(({ id, name }) => (
        <Tab key={id} value={id} label={name} />
      ))}
    </Tabs>
  );
};

const OffsetsHeader = ({ isUnlocked }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    sx={{ gap: 2 }}
  >
    <TitleBlock />
    {isUnlocked && (
      <Box sx={{ minWidth: 0, flexShrink: 1 }}>
        <PortfolioTabs />
      </Box>
    )}
  </Box>
);

const PurchasePageBlock = () => {
  const { portfolios, portfoliosLoading, portfoliosError } =
    usePortfoliosContext();
  const [purchasePortfolio, setPurchasePortfolio] = useState(null);

  useEffect(() => {
    analyticsPage("offsets");
    triggerGoogleEvent("view_item_list");
  }, []);

  if (portfoliosLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={4}>
        <AnimatedLogo />
      </Box>
    );
  }

  if (portfoliosError && !portfolios.length) {
    return <PortfolioLoadErrorBlock />;
  }

  return (
    <>
      {purchasePortfolio && (
        <PortfolioPurchaseFlow
          setPurchasePortfolio={setPurchasePortfolio}
          viewMode="company"
        />
      )}
      <PortfolioDisplayBlock setPurchasePortfolio={setPurchasePortfolio} />
      <CnaughtAttributionBanner />
    </>
  );
};

const Offsets = () => {
  const [accountData] = useAccountData();
  const { vendorSurveys = [], revenueYears = [] } = accountData || {};
  const { allEmissions = [] } = useEmissionsContext() || {};

  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);

  const { hasSurveyData, mostRecentSurveyYear, availableYears } = useMemo(
    () => deriveSurveyCompletion({ vendorSurveys }),
    [vendorSurveys]
  );

  const totalTons = useMemo(() => {
    if (!mostRecentSurveyYear) {
      return 0;
    }
    const yearSurveyEntry = findSurveyEntry(
      vendorSurveys,
      mostRecentSurveyYear
    );
    const surveyEmissions = filterEmissionsForYear(
      allEmissions,
      mostRecentSurveyYear
    );
    return computeTotalTons({
      yearSurveyEntry,
      revenueYears,
      activeYear: mostRecentSurveyYear,
      surveyEmissions
    });
  }, [vendorSurveys, allEmissions, revenueYears, mostRecentSurveyYear]);

  const surfaceContext = {
    hasSurveyData,
    totalTons,
    mostRecentSurveyYear,
    selectedYear: mostRecentSurveyYear,
    availableYears
  };

  const bodyComponents = {
    unlocked: PurchasePageBlock,
    locked: LockedOffsetsBlock
  };

  const BodyComponent = bodyComponents[hasSurveyData ? "unlocked" : "locked"];

  return (
    <FeatureErrorBoundary featureName="Carbon Offsets">
      <PortfoliosContextProvider
        displayPageUrl="/explorer/dashboard/offsets"
        selectedPortfolioId={selectedPortfolioId}
        onPortfolioSelect={setSelectedPortfolioId}
      >
        <Box display="flex" flexDirection="column" sx={{ gap: 3 }}>
          <OffsetsHeader isUnlocked={hasSurveyData} />
          <BodyComponent />
        </Box>
      </PortfoliosContextProvider>
      <ScoutFab surface="offsets" surfaceContext={surfaceContext} />
    </FeatureErrorBoundary>
  );
};
export default Offsets;
